import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from '~/components/icons/Icon';
import { Review } from '~/types/Review';

const AVATAR_COLORS = [
  '#4b5563', // slate gray
  '#0d9488', // teal
  '#7c3aed', // violet
  '#b45309', // amber
  '#1d4ed8', // blue
  '#be185d', // pink
  '#065f46', // green
  '#92400e', // brown
];

const getAvatarColor = (name: string): string => {
  const code = name.charCodeAt(0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const MAX_RATING = 5;

const ReviewCard = ({ author, text, rating }: Review) => {
  const { t, i18n } = useTranslation();
  const ratingLabel = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(rating);
  const initial = author.charAt(0).toUpperCase();
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [text]);

  return (
    <div className="review-card">
      <div className="review-card__header">
        <div
          className="review-card__author-avatar"
          style={{ backgroundColor: getAvatarColor(author) }}
          aria-hidden="true"
        >
          {initial}
        </div>
        <div className="review-card__author-info">
          <p className="review-card__author-name">{author}</p>
        </div>
      </div>
      <div className="review-card__body">
        <p className={`review-card__text${isExpanded ? ' review-card__text--expanded' : ''}`} ref={textRef}>
          {text}
        </p>
        {(isTruncated || isExpanded) && (
          <button className="review-card__read-more" onClick={() => setIsExpanded((v) => !v)}>
            {t(isExpanded ? 'components.reviews_block.read_less' : 'components.reviews_block.read_more')}
          </button>
        )}
      </div>
      <div className="review-card__footer">
        <div
          className="review-card__footer-stars"
          role="img"
          aria-label={t('components.reviews_block.rating_aria', { rating, max: MAX_RATING })}
        >
          {Array.from({ length: MAX_RATING }).map((_, index) => (
            <Icon
              name="star"
              size={20}
              key={index}
              aria-hidden="true"
              style={{ color: index < rating ? '#FDB022' : '#D0D5DD' }}
            />
          ))}
        </div>
        <span className="review-card__rating" aria-hidden="true">
          {ratingLabel}
        </span>
      </div>
    </div>
  );
};

export default ReviewCard;
